/**
 * The `__EMAIL__` sentinel — Reflecto's convention for "a mailto link
 * goes here" inside a translated string.
 *
 * Why a sentinel and not a real i18n placeholder: both Chrome's
 * `placeholders` mechanism and template functions substitute TEXT, but
 * these strings need a React ELEMENT (`<a href="mailto:…">`) injected
 * mid-sentence. Splitting the translation on a reserved token is the
 * standard interpolation pattern for that; the alternative — separate
 * before/after keys — would freeze the email's position in every
 * language.
 *
 * Guard rails around the convention:
 *  - `l10n/scripts/check-parity.ts` hard-fails any locale whose
 *    sentinel count differs from en (the token can move freely but
 *    must appear exactly as often).
 *  - The en catalogs' `description` fields warn translators the token
 *    is load-bearing.
 *  - Consumers split via {@link splitOnEmailSentinel}, never with an
 *    inline literal, so the token is defined in exactly one place.
 *
 * Degradation: if the token is absent (host passed a plain string),
 * `after` is `""` and the caller renders the whole text followed by
 * the link — a readable fallback rather than a crash or a dropped
 * email.
 */
export const EMAIL_SENTINEL = "__EMAIL__";

export interface EmailSentinelParts {
  /** Text before the sentinel (the whole string when the token is absent). */
  before: string;
  /** Text after the sentinel (`""` when the token is absent). */
  after: string;
}

/** Split a translated string on the {@link EMAIL_SENTINEL} token. */
export function splitOnEmailSentinel(text: string): EmailSentinelParts {
  const [before, after = ""] = text.split(EMAIL_SENTINEL);
  return { before, after };
}
